import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import { toast } from "react-toastify";
import UserImage from "../../assets/user.png";
import { api } from "../../app/Api/ApiSlice";

const PAGE_SIZE = 20;

const UserPostPage = () => {
    const { state } = useLocation();
    const { post } = state;

    // console.log(post)

    /* =======================
       MEDIA STATE
    ======================= */
    const imageFiles = useMemo(
        () => post.files?.filter(f => f.type === "image") || [],
        [post.files]
    );

    const [focusedImg, setFocusedImg] = useState(
        imageFiles.length ? imageFiles[0].url : ""
    );

    /* =======================
       COMMENTS STATE
    ======================= */
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState("");
    const [file, setFile] = useState(null);
    const [loadingSave, setLoadingSave] = useState(false);

    /* =======================
       PAGINATION STATE
    ======================= */
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loadingComments, setLoadingComments] = useState(false);

    /* =======================
       FETCH COMMENTS
    ======================= */
    const getComments = async (pageNumber = 1) => {
        try {
            setLoadingComments(true);

            const res = await api.get(
                `/posts/${post._id}/comments?page=${pageNumber}`
            );

            const newComments = res.data || [];

            setComments(prev =>
                pageNumber === 1 ? newComments : [...prev, ...newComments]
            );

            setHasMore(newComments.length === PAGE_SIZE);
        } catch (err) {
            toast.error("Failed to load comments");
        } finally {
            setLoadingComments(false);
        }
    };

    /* =======================
       RESET ON POST CHANGE
    ======================= */
    useEffect(() => {
        setPage(1);
        setHasMore(true);
        getComments(1);
    }, [post._id]);

    /* =======================
       MEDIA RENDERERS
    ======================= */
    const renderImages = () => (
        <>
            {focusedImg && (
                <div className="focused-image">
                    <img src={focusedImg} alt="post" />
                </div>
            )}

            {imageFiles.length > 1 && (
                <div className="image-selector">
                    {imageFiles.map((file, i) => (
                        <div
                            key={i}
                            className="image-selector-wrapper"
                            onClick={() => setFocusedImg(file.url)}
                        >
                            <img src={file.url} alt={file.name} />
                        </div>
                    ))}
                </div>
            )}
        </>
    );

    const renderVideo = file => (
        <div className="video-container">
            <video controls>
                <source src={file.url} type={file.mimeType} />
            </video>
        </div>
    );

    const renderDocuments = () => (
        <div className="document-container">
            {post.files.map((file, i) => {
                const ext = file.name.split(".").pop().toLowerCase();

                if (ext === "pdf") {
                    return (
                        <div key={i} className="document-preview">
                            <iframe src={file.url} title={file.name} loading="lazy" />
                            <a href={file.url} target="_blank" rel="noreferrer">
                                Open PDF
                            </a>
                        </div>
                    );
                }

                return (
                    <div key={i} className="document-download">
                        <span>{file.name}</span>
                        <a href={file.url} target="_blank" rel="noreferrer">
                            Download
                        </a>
                    </div>
                );
            })}
        </div>
    );

    const renderFiles = () => {
        if (!post.files?.length) return null;

        const firstFile = post.files[0];

        switch (firstFile.type) {
            case "image":
                return renderImages();
            case "video":
                return renderVideo(firstFile);
            case "document":
                return renderDocuments();
            default:
                return null;
        }
    };

    /* =======================
       SUBMIT COMMENT
    ======================= */
    const handleSubmit = async e => {
        e.preventDefault();

        if (!newComment && !file) {
            return toast.error("Comment or file required");
        }

        try {
            setLoadingSave(true);

            const formData = new FormData();
            formData.append("comment", newComment);
            if (file) formData.append("file", file);

            const res = await api.post(
                `/posts/${post._id}/comments`,
                formData
            );

            setComments(prev => [res.data, ...prev]);
            setNewComment("");
            setFile(null);

            toast.success("Comment added");
        } catch (err) {
            toast.error("Failed to add comment");
        } finally {
            setLoadingSave(false);
        }
    };

    /* =======================
       JSX
    ======================= */
    return (
        <div className="page-body">
            <div className="user-post-page">
                <div className="single-post-container">
                    {/* POST */}
                    <div className="single-post">
                        <div className="single-post-header">
                            <div className="post-user-info">
                                <img
                                    src={post.createdBy.profileImage || UserImage}
                                    alt=""
                                    className="post-profile-image"
                                />
                                <div>
                                    <p className="post-username">
                                        {post.createdBy.firstname} {post.createdBy.lastname}
                                    </p>
                                    <span className="post-timestamp">
                                        {new Date(post.createdAt).toLocaleDateString()}
                                    </span>
                                    <p className="post-audience">{post.audience}</p>
                                </div>
                            </div>
                        </div>

                        <div className="single-post-content">
                            <p className="single-postpage-post-text">{post.postText}</p>
                            {renderFiles()}
                        </div>
                    </div>

                    {/* COMMENTS */}
                    <div className="comments-section">
                        <h3>Comments</h3>

                        <form className="comment-form" onSubmit={handleSubmit}>
                            <textarea
                                placeholder="Write a comment..."
                                value={newComment}
                                onChange={e => setNewComment(e.target.value)}
                            />
                            <input type="file" onChange={e => setFile(e.target.files[0])} />
                            <button type="submit" disabled={loadingSave}>
                                {loadingSave ? "Saving..." : "Submit"}
                            </button>
                        </form>

                        <InfiniteScroll
                            dataLength={comments.length}
                            next={() => {
                                const nextPage = page + 1;
                                setPage(nextPage);
                                getComments(nextPage);
                            }}
                            hasMore={hasMore}
                            loader={<p>Loading more comments...</p>}
                            endMessage={<p style={{ textAlign: "center" }}>No more comments</p>}
                        >
                            <ul className="comments-list">
                                {comments.map(comment => (
                                    <li key={comment._id} className="comment">
                                        {/* {console.log("c:",comment)} */}
                                        <div className="comment-header">
                                            <img
                                                src={comment.user?.profilePic?.url || UserImage}
                                                alt=""
                                                className="comment-profile-image"
                                            />
                                            <span>
                                                {comment.user.firstname} {comment.user.lastname}
                                            </span>
                                            <span>
                                                {new Date(comment.createdAt).toLocaleDateString()}
                                            </span>
                                        </div>
                                        <p>{comment.comment}</p>
                                    </li>
                                ))}
                            </ul>
                        </InfiniteScroll>

                        {!comments.length && !loadingComments && (
                            <p>No comments yet.</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserPostPage;
